import type {
  BundleImportItem,
  BundleImportPreview,
} from '@/lib/api/generated/model';

import {
  decidable,
  itemId,
  unanswered,
  type BundleChoices,
  type ItemChoice,
} from './bundleAnswers';

/**
 * What a bundle's import review shows, questions first (spec 208).
 *
 * In the review's scene 2 of 9 items need the person; the rest are new or
 * already here. So the review leads with a one-line summary and the questions,
 * and folds everything else into one row per group. Pure, so the grouping and
 * the words are tested without a dialog.
 */
export interface ImportReview {
  /** "9 items · 7 new · 2 need your answer"; zero counts are left out. */
  summary: string;
  /** Items the person decides about, in the service's order. */
  questions: BundleImportItem[];
  /** Items new to this workspace. */
  fresh: BundleImportItem[];
  /** Items already here with the same content, which need nothing. */
  here: BundleImportItem[];
  /** Questions still to answer: items, plus profiles and people outstanding. */
  open: number;
  /** The first open item question's id, `references` when only profiles or people remain. */
  firstOpen: string | null;
}

export function reviewOf(
  preview: BundleImportPreview,
  choices: BundleChoices,
  referencesLeft: number,
): ImportReview {
  const questions = decidable(preview.items);
  const fresh = preview.items.filter((item) => item.state === 'new');
  const here = preview.items.filter(
    (item) => item.state === 'existing' && item.answer == null,
  );
  const openItems = unanswered(preview.items, choices);
  const open = openItems.length + referencesLeft;

  const parts = [counted(preview.items.length, 'item', 'items')];
  if (fresh.length > 0) parts.push(`${String(fresh.length)} new`);
  if (here.length > 0) parts.push(`${String(here.length)} already here`);
  if (open > 0) {
    parts.push(`${String(open)} ${open === 1 ? 'needs' : 'need'} your answer`);
  }

  const first = openItems[0];
  return {
    summary: parts.join(' · '),
    questions,
    fresh,
    here,
    open,
    firstOpen: first ? itemId(first) : referencesLeft > 0 ? 'references' : null,
  };
}

const KINDS: readonly [BundleImportItem['kind'], string, string][] = [
  ['workflow', 'workflow', 'workflows'],
  ['agent', 'agent', 'agents'],
  ['skill', 'skill', 'skills'],
];

/** "7 new: 2 workflows, 5 agents", "1 already here: 1 agent". */
export function groupLabel(
  group: 'new' | 'here',
  items: readonly BundleImportItem[],
): string {
  const byKind = KINDS.flatMap(([kind, one, many]) => {
    const count = items.filter((item) => item.kind === kind).length;
    return count > 0 ? [counted(count, one, many)] : [];
  });
  const head = `${String(items.length)} ${group === 'new' ? 'new' : 'already here'}`;
  return byKind.length > 0 ? `${head}: ${byKind.join(', ')}` : head;
}

export type MarkerTone = 'neutral' | 'success' | 'warning';

/**
 * The word on an item, and the colour it carries. A question follows the
 * person's choice at once, before the next check confirms it, so answering is
 * seen as answered immediately.
 */
export function markerOf(
  item: BundleImportItem,
  choice: ItemChoice | undefined,
): { word: string; tone: MarkerTone } {
  const question = item.state === 'conflict' || item.answer != null;
  if (question) {
    if (choice?.action === 'use_existing') {
      return { word: 'Use ours', tone: 'neutral' };
    }
    if (choice?.action === 'replace') {
      return { word: 'Will replace', tone: 'neutral' };
    }
    if (choice?.action === 'update') {
      return { word: 'Will update', tone: 'neutral' };
    }
    if (choice?.action === 'rename' && choice.newKey.trim() !== '') {
      return { word: `As ${choice.newKey.trim()}`, tone: 'neutral' };
    }
    return { word: 'Needs your answer', tone: 'warning' };
  }
  if (item.state === 'existing')
    return { word: 'Already here', tone: 'success' };
  return { word: 'New', tone: 'neutral' };
}

/** "Use the ones here for all": every item question answered with the one here. */
export function answerAllWithOurs(
  questions: readonly BundleImportItem[],
  choices: BundleChoices,
): BundleChoices {
  const next: BundleChoices = { ...choices };
  for (const item of questions) next[itemId(item)] = { action: 'use_existing' };
  return next;
}

/** "1 item", "9 items". */
export function counted(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
